import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { of, combineLatest, EMPTY, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap, take, delay } from 'rxjs/operators';
import { getCurrentPractice } from 'src/app/practices/state/selectors';
import { getDeeplinkProcessing } from './selectors';
import { AppState } from 'src/app/state/reducers';
import { DeeplinkHandlersService } from '../deeplink-handlers.service';
import * as DeeplinkActions from './actions';

@Injectable()
export class DeeplinkHandlersEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private deeplinkHandlersService: DeeplinkHandlersService,
  ) {}

  processProvetSendInvoice$ = createEffect(() => this.actions$.pipe(
    ofType(DeeplinkActions.ProcessProvetSendInvoice),
    switchMap((action) => 
      this.store.select(getCurrentPractice).pipe(
        filter(practice => {
          // Only proceed if we have a practice
          if (practice == null) {
            console.log('ProcessProvetSendInvoice: Waiting for practice to be available');
            return false;
          }
          return true;
        }),
        take(1), // Only take the first valid practice
        switchMap(practice => {
          console.log('ProcessProvetSendInvoice: Making HTTP call for practice:', practice!.id);
          return this.deeplinkHandlersService.processProvetSendInvoice(practice!.id.toString(), action.params)
            .pipe(
              switchMap((response: any) => {
                // Handle error response
                if (response.error) {
                  return of(DeeplinkActions.ProcessProvetSendInvoiceFail({ 
                    error: `Error code: ${response.error}`,
                    errorCode: response.error
                  }));
                }

                // Handle success response
                if (response.success) {
                  // If we have a conversationId, navigate to the conversation after 5-second delay
                  if (response.conversationId) {
                    timer(5000).subscribe(() => {
                      this.router.navigate(['/conversations', response.conversationId]);
                    });
                  }
                  return of(DeeplinkActions.ProcessProvetSendInvoiceSuccess({ response }));
                }

                // Unexpected response format  
                return of(DeeplinkActions.ProcessProvetSendInvoiceFail({ 
                  error: 'Unexpected response format',
                  errorCode: null
                }));
              }),
              catchError((error) => {
                console.error('Error processing provet send invoice:', error);
                
                // Check if we have a specific error code from the response
                let errorCode: number | null = null;
                if (error.error && error.error.errorCode) {
                  errorCode = error.error.errorCode;
                } else if (error.status) {
                  // Map HTTP status codes to our error codes if needed
                  switch (error.status) {
                    case 400:
                      errorCode = 30001; // Invalid data
                      break;
                    case 401:
                      errorCode = 30002; // Authentication failed
                      break;
                    case 403:
                      errorCode = 30003; // Access denied
                      break;
                    case 404:
                      errorCode = 30004; // Not found
                      break;
                    case 408:
                      errorCode = 30005; // Timeout
                      break;
                    case 500:
                      errorCode = 30008; // Service error
                      break;
                    case 503:
                      errorCode = 30007; // Service unavailable
                      break;
                    default:
                      errorCode = 30008; // General service error
                      break;
                  }
                }

                return of(DeeplinkActions.ProcessProvetSendInvoiceFail({ 
                  error: error.message || 'Failed to process deeplink',
                  errorCode: errorCode
                }));
              })
            );
        })
      )
    )
  ));
}